import { Badge } from '@/components/Atoms/Badge/Badge';
import { BadgeList } from '@/components/Atoms/BadgeList/BadgeList';
import { Button } from '@/components/Atoms/Button/Button';
import { MarkedText } from '@/components/Atoms/MarkedText/MarkedText';
import { MediaCardImage } from '@/components/Atoms/MediaCardImage/MediaCardImage';
import { ProfilePicture } from '@/components/Atoms/ProfilePicture/ProfilePicture';
import { TimeAgo } from '@/components/Atoms/TimeAgo/TimeAgo';
import { Card, CardActions, CardBody, CardImage, CardMetadata } from '@/components/Molecules/Card/Card';
import { ContentStatus } from '@/components/Molecules/ContentStatus/ContentStatus';
import { ContentTypeHeader } from '@/components/Molecules/ContentTypeHeader/ContentTypeHeader';
import { SaveButton } from '@/components/Molecules/SaveButton/SaveButton';
import useTranslation from '@/hooks/useTranslation';
import type { IContentItem, IEventSession, IPerson } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { ResponsiveImageHints } from '@/services/ResponsiveImageHints';
import classNames from '@/utils/classNames';
import ClockIcon from '@bloomberg/web-icons-react/Clock';
import PaperclipIcon from '@bloomberg/web-icons-react/Paperclip';
import { useId } from 'react';
import type { FCl } from 'react-fc';
import styles from './EventSessionCard.module.css';
export interface IAvailableSession {
  item: IEventSession | IContentItem;
  participants?: IPerson[];
}
export const EventSessionCard: FCl<IAvailableSession> = ({
  item,
  participants,
  className
}) => {
  const {
    t
  } = useTranslation();
  const titleId = useId();
  const analyticsBaseId = `${item.type}-${item.id}`;
  const participantsWithImages = (participants || []).filter(participant => participant.imageUrl);
  const attachmentValues = new Array<string>();
  if ((item as IEventSession).hasData) {
    attachmentValues.push('Excel');
  }
  if ((item as IEventSession).hasSlides) {
    attachmentValues.push('PowerPoint');
  }
  const attachmentString = attachmentValues.join(', ');
  const showPlayButton = Boolean(item.type === 'video' || item.videoDuration);
  const hasParticipants = participantsWithImages.length > 0;
  const hasAttachments = attachmentValues.length > 0;
  const hasMetadata = Boolean(item.displayDate) || hasAttachments;
  const hasBody = hasParticipants || hasMetadata;
  return <Card id={titleId} title={<a className="bbui-card-primary-action" href={item.url}>
                    <MarkedText value={item.title} />
                </a>} headingLevel="h4" responsive className={classNames(styles.container, className)} data-analytics-tracking={buildTrackingObject(TrackingAction.FollowLink, TrackingCategory.BnefContent, analyticsBaseId)} prefix={<BadgeList>
                    {item.parentSeries && <Badge text={t('ContentType.series')} link="/series" colorVar="blue" />}
                    <ContentTypeHeader type={item.type} eventSubtype={item.eventSubtype} />
                    <ContentStatus status={item.contentStatus} />
                </BadgeList>}>
            {item.leadImage && <CardImage>
                    <MediaCardImage imageHints={new ResponsiveImageHints({
        initial: '100vw',
        atM: '50vw',
        atXL: '25vw'
      }, null, 16 / 9)} url={item.leadImage} showPlayButton={showPlayButton} durationSeconds={item.videoDuration} />
                </CardImage>}
            {hasBody && <CardBody>
                    {hasParticipants && <div className={styles.sessionData}>
                            <div className={styles.participants}>
                                {participantsWithImages.map((participant_0, index) => <a href={participant_0.url} key={participant_0.displayName} className={styles.participantLink} style={{
            ['--participant-index' as string]: index
          }} aria-label={`View ${participant_0.displayName}'s profile`}>
                                        <ProfilePicture className={styles.participant} src={participant_0.imageUrl} alt={participant_0.name} />
                                    </a>)}
                            </div>
                        </div>}
                    {hasMetadata && <CardMetadata items={[item.displayDate ? {
        icon: <ClockIcon />,
        label: t('common:Labels.published'),
        value: <TimeAgo datetime={item.displayDate} />
      } : undefined, hasAttachments ? {
        icon: <PaperclipIcon />,
        label: t('common:SearchFilter.filterNames.attachment'),
        value: attachmentString
      } : undefined]} />}
                </CardBody>}
            <CardActions>
                {(item as IEventSession).hasSlides && <Button key="view-slides" href={`${item.url}/slides`} icon={<PaperclipIcon />} kind="tertiary">
                        {t('Actions.view_slides')}
                    </Button>}
                <SaveButton aria-describedby={titleId} id={item.id} type="eventsession" analyticsBaseId={analyticsBaseId} />
            </CardActions>
        </Card>;
};